import type { ReactElement, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuthSession } from '@/lib/api';
import { LoginScreen } from '@/components/auth/LoginScreen';

/**
 * Gates the entire app on an authenticated session.
 *
 * Mounted inside `BootGate` (so the backend is ready) and above `ProjectProvider`
 * (so no auth-required fetch — codebases, conversations — fires before login).
 * Shares the `['authSession']` query key with `SettingsPage`, so a successful
 * login invalidates one cache entry and the app re-renders authenticated.
 */
export function AuthGate({ children }: { children: ReactNode }): ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['authSession'],
    queryFn: getAuthSession,
    retry: 2,
  });

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background text-sm text-text-secondary">
        Loading…
      </div>
    );
  }

  // A thrown query means the server is unreachable — the session endpoint returns
  // 200 with authenticated:false when it's up. Surface it explicitly instead of
  // falling through to the login screen, which would strand the operator with no
  // explanation during a backend restart.
  if (isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6 text-center">
        <div>
          <p className="text-sm text-error">Unable to reach the Maestro server.</p>
          <p className="mt-1 text-xs text-text-tertiary">
            Check that the server is running, then refresh.
          </p>
        </div>
      </div>
    );
  }

  if (!data?.authenticated) {
    return <LoginScreen bootstrapRequired={data?.bootstrapRequired ?? false} />;
  }

  return <>{children}</>;
}
